import React, { useState, useEffect } from 'react';
import { ShieldCheck, Cpu, CheckCircle2, User } from 'lucide-react';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { db, auth } from '@/firebase';
import { CURRENT_DISCLAIMER_VERSION } from '../views/DisclaimerPage';
import { useTranslation } from 'react-i18next';

/**
 * 🖥️ BootScreen
 * Refactored for Hybrid Scoping: Supports both Shared (Household) 
 * and Personal (User-nested) data initialization.
 */
export const BootScreen: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<string[]>([]);
  const [isAnimationDone, setIsAnimationDone] = useState(false);
  const [isUserReady, setIsUserReady] = useState(false);
  const [isDataScopeReady, setIsDataScopeReady] = useState(false);
  const [scopeMode, setScopeMode] = useState<'PERSONAL' | 'SHARED' | 'PENDING'>('PENDING');
  
  const sequence = [
    t('boot_screen.init_kernel', 'Initializing EverydayOS Kernel...'),
    t('boot_screen.secure_handshake', 'Establishing Secure Handshake...'),
    t('boot_screen.secure_protocol', 'Connecting to Secure Protocol...'),
    t('boot_screen.authorize_credentials', 'Authorizing User Credentials...'),
    t('boot_screen.resolve_data_scope', 'Resolving Data Scope Mode...'),
    t('boot_screen.verify_encrypted_storage', 'Verifying Encrypted Storage...'),
    t('boot_screen.sync_neural_knowledge', 'Syncing Neural Knowledge Sparks...'),
    t('boot_screen.optimize_dashboard', 'Optimizing Dashboard Modules...'),
    t('boot_screen.validate_cloud_propagation', 'Validating Cloud Propagation...'), 
    t('boot_screen.system_ready', 'System Ready. Finalizing Handover...')
  ];

  // 1. 🎬 Visual Log Animation
  useEffect(() => {
    const totalDuration = 8000; // Optimized for performance
    const interval = totalDuration / sequence.length;

    sequence.forEach((text, i) => {
      setTimeout(() => {
        setLogs(prev => [...prev, text]);
        if (i === sequence.length - 1) setIsAnimationDone(true);
      }, i * interval);
    });
  }, []);

  // 2. 🛰️ Hybrid Scope Verification (Dual Gate)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // GATE 1: Profile & Privacy Verification
    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists() && snap.data()?.acceptedDisclaimerVersion === CURRENT_DISCLAIMER_VERSION) {
        setIsUserReady(true);
      }
    });

    // GATE 2: Scope Resolution (Personal Mode Only - Households not required at boot)
    setScopeMode('PERSONAL');
    setIsDataScopeReady(true);

    return () => {
      unsubUser();
    };
  }, []);

  // 3. 🏁 Final Handover Logic
  useEffect(() => {
    if (isAnimationDone && isUserReady && isDataScopeReady) {
      const redirectTimer = setTimeout(() => {
        // Clear session logs and transition to the Resolved Scope
        window.location.href = '/dashboard';
      }, 800);
      return () => clearTimeout(redirectTimer);
    }
  }, [isAnimationDone, isUserReady, isDataScopeReady]);

  return (
    <div className="h-screen w-screen bg-slate-950 flex flex-col items-center justify-center p-6 overflow-hidden select-none">
      
      {/* 🌌 Dynamic Glow Backdrop */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-500/10 via-transparent to-transparent pointer-events-none" />

      {/* 🛡️ Central Icon */}
      <div className="relative mb-12">
        <div className="absolute inset-0 bg-indigo-500/20 blur-3xl rounded-full animate-pulse" />
        <ShieldCheck className="w-20 h-20 text-brand relative z-10 animate-in zoom-in duration-700" />
      </div>

      <div className="w-full max-w-sm space-y-4 relative z-10">
        {/* 📜 Terminal Output */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 font-mono min-h-[280px] shadow-2xl transition-all duration-500">
          {logs.map((log, i) => (
            <div key={i} className="flex items-center gap-3 mb-2 animate-in slide-in-from-left-2 fade-in duration-300">
              {i === logs.length - 1 && (!isAnimationDone || !isUserReady || !isDataScopeReady) ? (
                <Cpu className="w-3.5 h-3.5 text-indigo-400 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              )}
              <span className={`text-2xs uppercase font-bold tracking-widest leading-tight ${i === logs.length - 1 ? 'text-white' : 'text-slate-500'}`}>
                {log}
              </span>
            </div>
          ))}

          {/* 🔍 Protocol Status Bars */}
          {isAnimationDone && (!isUserReady || !isDataScopeReady) && (
            <div className="mt-4 pt-4 border-t border-slate-800 space-y-2 animate-in fade-in duration-500">
              <div className="flex items-center justify-between text-[8px] uppercase font-black tracking-widest">
                <span className="text-slate-500">{t('boot_screen.user_identity', 'User Identity:')}</span>
                <span className={isUserReady ? "text-emerald-500" : "text-amber-500 animate-pulse"}>
                  {isUserReady ? t('boot_screen.verified', 'Verified') : t('boot_screen.authorizing', 'Authorizing...')}
                </span>
              </div>
              <div className="flex items-center justify-between text-[8px] uppercase font-black tracking-widest">
                <span className="text-slate-500">{t('boot_screen.scope_resolution', 'Scope Resolution:')}</span>
                <span className={isDataScopeReady ? "text-indigo-400" : "text-amber-500 animate-pulse"}>
                  {isDataScopeReady ? scopeMode : "Resolving..."}
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* 📊 Progress Bar Container */}
        <div className="space-y-3">
          <div className="h-1.5 w-full bg-slate-800/50 rounded-full overflow-hidden border border-slate-700/30">
            <div 
              className="h-full bg-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.5)] transition-all duration-1000 ease-linear"
              style={{ 
                width: `${(isAnimationDone && isUserReady && isDataScopeReady) ? 100 : (logs.length / sequence.length) * 90}%` 
              }}
            />
          </div>

          <div className="flex justify-between items-center px-1 text-[8px] font-black text-slate-500 uppercase tracking-widest">
              <div className="flex gap-4">
                <span>MEM: 1024GB</span>
                <span>MODE: {scopeMode}</span>
              </div>
              <span className="text-brand/50 animate-pulse flex items-center gap-1">
                <User className="w-2 h-2" /> {t('boot_screen.privacy_lockdown', 'Privacy Lockdown')}
              </span>
          </div>
        </div>
      </div>

      <p className="mt-12 text-2xs font-black uppercase tracking-[0.4em] text-slate-700 animate-pulse">
        Everyday OS v1.0.4 • Hybrid Boot Sequence
      </p>
    </div>
  );
};

export default BootScreen;